import { createInertiaApp } from '@inertiajs/react'
import { type ComponentType, createElement, type ReactNode } from 'react'
import { createRoot } from 'react-dom/client'

type LayoutComponent = ComponentType & { layout?: (page: ReactNode) => ReactNode }
type PageModule = { default: LayoutComponent }
type Pages = Record<string, () => Promise<unknown>>

/**
 * Mounts one Inertia surface (marketing | operator | portal). Each surface
 * passes its own `import.meta.glob` of pages and a shell layout, so the surfaces
 * are separate bundles that never import each other's screens
 * (docs/architecture.md §4.1, §7).
 */
export function mountSurface(options: { pages: Pages; layout: (page: ReactNode) => ReactNode }) {
  void createInertiaApp({
    resolve: (name) => {
      const match = Object.keys(options.pages).find((path) => path.endsWith(`/${name}.tsx`))
      if (!match) throw new Error(`Inertia page not found: ${name}`)
      return (options.pages[match]() as Promise<PageModule>).then((page) => {
        page.default.layout ??= options.layout
        return page.default
      })
    },
    setup({ el, App, props }) {
      createRoot(el).render(createElement(App, props))
    },
  })
}
